import { useEffect, useCallback } from 'react';
import { X, Mail, CircleCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useResendVerificationEmailMutation } from '@/features/auth/api/authApi';
import { useOtpCooldown } from '@/shared/hooks/useOtpCooldown';
import toast from 'react-hot-toast';

interface VerifyEmailModalProps {
  open: boolean;
  onClose: () => void;
  email?: string;
}

export default function VerifyEmailModal({
  open,
  onClose,
  email = 'numan@slc.digital',
}: VerifyEmailModalProps) {
  const { t } = useTranslation('login');
  const [resendVerificationEmail, { isLoading }] =
    useResendVerificationEmailMutation();
  const COOLDOWN_STORAGE_KEY = `email_verification_cooldown_${email}`;
  const {
    resendCooldownSeconds,
    setResendCooldownSeconds,
    handleResendError,
    handleClose: clearCooldown,
  } = useOtpCooldown(COOLDOWN_STORAGE_KEY);

  const handleClose = useCallback(() => {
    clearCooldown();
    onClose();
  }, [onClose, clearCooldown]);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    if (open) window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [open, handleClose]);

  const handleResendEmail = async () => {
    if (!email || resendCooldownSeconds > 0) {
      return;
    }

    try {
      const response = await resendVerificationEmail({ email }).unwrap();

      // Start cooldown
      const retrySeconds = response.retryAfterSeconds || 300;
      setResendCooldownSeconds(retrySeconds);
      localStorage.setItem(
        COOLDOWN_STORAGE_KEY,
        (Date.now() + retrySeconds * 1000).toString()
      );

      toast.success('Verification email sent successfully!');
    } catch (error: unknown) {
      const { message } = handleResendError(error);
      toast.error(message);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={handleClose}
    >
      <div
        role="dialog"
        data-state="open"
        data-slot="dialog-content"
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        className="bg-white data-[state=open]:animate-in data-[state=closed]:animate-out 
                   data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 
                   data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95
                   fixed top-[50%] left-[50%] z-50 grid w-full max-w-[calc(100%-2rem)] 
                   translate-x-[-50%] translate-y-[-50%] gap-4 rounded-lg border p-6 
                   shadow-lg duration-200 sm:max-w-md"
      >
        {/* Close button */}
        <button
          type="button"
          className="ring-offset-background focus:ring-ring data-[state=open]:bg-accent 
                     data-[state=open]:text-muted-foreground absolute top-4 right-4 
                     rounded-xs opacity-70 transition-opacity hover:opacity-100 
                     focus:ring-2 focus:ring-offset-2 focus:outline-hidden 
                     disabled:pointer-events-none [&>svg]:pointer-events-none 
                     [&>svg]:shrink-0 [&>svg:not([class*='size-'])]:size-4"
          onClick={handleClose}
        >
          <X className="w-5 h-5" />
          <span className="sr-only">{t('close')}</span>
        </button>

        {/* Header */}
        <div
          data-slot="dialog-header"
          className="flex flex-col gap-2 text-center sm:text-left"
        >
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-slate-900 rounded-full flex items-center justify-center">
              <Mail className="w-8 h-8 text-white" />
            </div>
          </div>
          <h2
            data-slot="dialog-title"
            className="font-semibold text-center text-2xl text-slate-900"
          >
            {t('verifyEmailTitle')}
          </h2>
          <p
            data-slot="dialog-description"
            className="text-sm text-center text-slate-500"
          >
            {t('verifyEmailDescription', { email })}
          </p>
        </div>

        {/* Body */}
        <div className="space-y-5 py-4">
          <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4">
            <div className="flex flex-col gap-2">
              <button
                type="button"
                onClick={handleResendEmail}
                disabled={isLoading}
                data-slot="button"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium transition-all 
                           disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none 
                           [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none 
                           focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] 
                           aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 
                           aria-invalid:border-destructive px-4 py-2 has-[>svg]:px-3 w-full h-14 
                           bg-[rgb(0,153,255)] hover:bg-blue-700 text-white rounded-full shadow-lg shadow-blue-600/30 cursor-pointer"
              >
                <span className="flex items-center gap-2">
                  <CircleCheck className="w-5 h-5" />
                  {t('verifyEmailButton')}
                </span>
              </button>
              {resendCooldownSeconds > 0 && (
                <p className="text-xs text-slate-600 text-center">
                  {t('verifyEmailButton')} in (
                  {Math.floor(resendCooldownSeconds / 60)}:
                  {String(resendCooldownSeconds % 60).padStart(2, '0')})
                </p>
              )}
            </div>

            <p className="text-xs text-slate-600 text-center mt-3">
              {t('verifyEmailHint')}
            </p>
          </div>

          {/* <div className="text-center text-sm text-slate-600">
            {t('didntReceiveEmail')}?{' '}
            <button
              type="button"
              onClick={handleResendEmail}  // also attach here
              disabled={isLoading}
              className="text-blue-600 hover:underline disabled:opacity-50"
            >
              {t('resend')}
            </button>
          </div> */}

          <p className="text-xs text-center text-slate-500 pt-2">
            {t('checkSpam')}
          </p>
        </div>
      </div>
    </div>
  );
}
